import { Popover, Typography, Button, InputNumber } from 'antd'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'next-i18next'
import { useClickAway, useKey } from 'react-use'

import { Placement } from './utils'

interface PauseMenuProps {
  position: {
    placement: Placement
    dimension: {
      top: number | string
      bottom: number | string
      left: number | string
      right: number | string
    }
  }
  duration: number
  onSave: (duration: number) => void
  onClose?: () => void
}

const MIN_DURATION = 0
/**
 * 기존 코드에는 제한이 없는 것 같은데\
 * 임의로 1시간으로 제한함
 */
const MAX_DURATION = 60 * 60 // 1 hour
const STEP = 0.5

const PauseMenu: FC<PauseMenuProps> = ({
  position,
  duration: initialDuration,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation('common')

  const [duration, setDuration] = useState(initialDuration)

  useEffect(() => {
    setDuration(initialDuration)
  }, [initialDuration])

  const handleChangeDuration = (value: number | null) => {
    const clampedValue = Math.max(
      MIN_DURATION,
      Math.min(MAX_DURATION, value || MIN_DURATION),
    )

    const steppedValue = Math.round(clampedValue / STEP) * STEP

    setDuration(steppedValue || MIN_DURATION)
  }

  const handleClickSave = () => {
    onSave(duration)
    onClose?.()
  }

  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [initialDuration])

  /**
   * 팝업 바깥 영역을 클릭했을 때 닫히게 하는 로직
   */
  const popoverRef = useRef<HTMLElement | null>(null)
  const POPOVER_CLASS_NAME = 'pause-popover'

  useEffect(() => {
    popoverRef.current = document.querySelector<HTMLElement>(
      `.${POPOVER_CLASS_NAME}`,
    )
  }, [])

  const handleClickOutside = useCallback(() => {
    onClose?.()
  }, [onClose])

  useClickAway(popoverRef, handleClickOutside, ['pointerdown'])
  useKey('Escape', handleClickOutside)

  return (
    <Popover
      overlayClassName={POPOVER_CLASS_NAME}
      arrow={true}
      open={true}
      overlayStyle={{
        ...position.dimension,
        minWidth: 'auto',
        height: 'auto',
      }}
      placement={position.placement}
      content={() => {
        return (
          <ModalWrapper>
            <StyledTitle level={5}>{t('Pause')}</StyledTitle>
            <InputArea>
              <InputNumber
                ref={inputRef}
                value={duration}
                min={MIN_DURATION}
                max={MAX_DURATION}
                step={STEP}
                onChange={handleChangeDuration}
                onPressEnter={handleClickSave}
              />
              <span className="unit">s</span>
            </InputArea>
            <div className="buttonWrapper">
              <Button size="large" type="primary" onClick={handleClickSave}>
                {t('Save')}
              </Button>
            </div>
          </ModalWrapper>
        )
      }}
    />
  )
}

export default PauseMenu

const ModalWrapper = styled.div`
  display: flex;
  flex-flow: column;
  gap: 5px;

  padding: 0 5px 5px 5px;

  h5 {
    text-align: center;
  }

  .buttonWrapper {
    display: flex;
    justify-content: end;
  }
`

const StyledTitle = styled(Typography.Title)`
  margin: 0;
`

const InputArea = styled.div`
  display: flex;
  align-items: end;
  gap: 4px;

  .unit {
    flex: 0;
  }
`
